import React, { useCallback, useContext, useEffect, useState } from "react";
import PropTypes from "prop-types";
import { useHistory } from "react-router-dom";
import { toast } from "react-toastify";
import { ShoppingCart } from "phosphor-react";
import { fetchCartItems, removeToCart, removeAllItemsToCart } from "api/Cart";
import { getFarm } from "api/Farm";
import { useBasePricing } from "hooks/useBasePricing";
import { UserContext } from "context/user";
import { getItemPrice, getStackItemPrice } from "utils";
import Loading from "components/Loading";
import CustomModal from "components/Modal";
import Button from "components/Button";
import Accordion from "components/Accordion";
import "./index.scss";
import { useStackBasePriceQuery } from "hooks/useStackBasePriceQuery";

const CartModal = ({ isOpen, onClose }) => {
  const { token } = useContext(UserContext);
  const [cartItems, setCartItems] = useState(null);
  const [cartIsLoading, setCartIsLoading] = useState(false);
  const [totalPricing, setTotalPricing] = useState("");
  const [farm, setFarm] = useState(null);
  const history = useHistory();
  const { data } = useBasePricing(token);
  const { data: stackPricing } = useStackBasePriceQuery(token);

  const getCartDetails = useCallback(
    async (items) => {
      const farmData = [];
      await Promise.all(
        items.map(async (item) => {
          try {
            const farmDetails = await getFarm(item.details.farm_id, token);
            farmData.push(farmDetails);
          } catch (e) {
            toast.error("Fetching Farm details error.");
          }
        })
      );
      const itemsInCart = [];
      items.forEach((item) => {
        if (item.item_type === "zoning") {
          farmData.forEach((i) => {
            if (i.properties.farm_id === item.details.farm_id) {
              const price = getItemPrice(
                item?.details?.zones?.features,
                data?.subscriber_discounted_cost ?? data?.hectare_cost
              );
              const originalPrice = data?.subscriber_discounted_cost
                ? getItemPrice(
                    item?.details?.zones?.features,
                    data?.hectare_cost
                  )
                : undefined;
              itemsInCart.push({
                item_type: item.item_type,
                details: item.details,
                id: item.id,
                farm: i,
                price,
                original_price: originalPrice,
              });
            }
          });
        }
        if (item.item_type === "stack analysis order") {
          farmData.forEach((i) => {
            if (i.properties.farm_id === item.details.farm_id) {
              const price = getStackItemPrice(
                item?.details,
                stackPricing?.subscriber_discounted_cost ??
                  stackPricing?.unit_cost
              );
              const originalPrice = stackPricing?.subscriber_discounted_cost
                ? getStackItemPrice(item?.details, stackPricing?.unit_cost)
                : undefined;
              itemsInCart.push({
                item_type: item.item_type,
                details: item.details,
                id: item.id,
                farm: i,
                price,
                original_price: originalPrice,
              });
            }
          });
        }
      });
      const result = [
        ...itemsInCart
          .reduce((a, c) => {
            a.set(c.id, c);
            return a;
          }, new Map())
          .values(),
      ];
      setCartItems(result);
    },
    [
      token,
      data?.hectare_cost,
      stackPricing?.unit_cost,
      data?.subscriber_discounted_cost,
      stackPricing?.subscriber_discounted_cost,
    ]
  );

  const getTotalPrice = useCallback(async () => {
    let subTotal = 0;
    await cartItems.map(async (cart) => {
      subTotal += cart?.price;
    });
    setTotalPricing(subTotal.toFixed(2));
    return subTotal.toFixed(2);
  }, [cartItems]);

  useEffect(() => {
    if (!cartItems) return;
    getTotalPrice();
  }, [cartItems, getTotalPrice]);

  const fetchItems = useCallback(async () => {
    if (!token) return;
    setCartIsLoading(true);
    try {
      const response = await fetchCartItems(token);
      if (response) {
        setCartIsLoading(false);
        getCartDetails(response);
      }
    } catch (error) {
      toast.error(error);
    }
  }, [token, getCartDetails]);

  useEffect(() => {
    if (cartItems) return;
    if (!cartItems) {
      fetchItems();
    }
  }, [cartItems, fetchItems]);

  const handleOnClose = () => {
    onClose();
  };

  const handleRemoveToCart = useCallback(
    async (id) => {
      try {
        const response = await removeToCart(id, token);
        if (response.detail) {
          toast.success("Item removed successfully!");
          const newData = cartItems.filter((item) => item.id !== id);
          setCartItems(newData);
        }
      } catch (e) {
        toast.error(e);
      }
    },
    [token, cartItems, setCartItems]
  );

  const handleDeleteAll = useCallback(async () => {
    try {
      const respo = await removeAllItemsToCart(token);
      if (respo) {
        toast.success("Your cart items removed successfuly");
        setCartItems(null);
      }
    } catch (e) {
      toast.error(e);
    }
  }, [token]);

  const handleCheckOut = () => {
    history.push("/payment");
  };

  return (
    <CustomModal
      isOpen={isOpen}
      size="large"
      onClose={handleOnClose}
      onRequestClose={handleOnClose}
      blurred
    >
      <div className="cart" data-testid="">
        <h3 className="title">Your cart</h3>
        <div className="cart-header" />
        {cartIsLoading ? (
          <Loading className="cart-loading" />
        ) : (
          <>
            {!cartItems?.length ? (
              <div className="empty-body">
                <ShoppingCart size={32} color="#83858E" />
                <p className="paragraph">You have no items in your cart</p>
              </div>
            ) : (
              <>
                <div className="cart-body">
                  <div className="cart-content">
                    <Accordion
                      elements={cartItems}
                      removeItem={handleRemoveToCart}
                      farm={farm}
                      setFarm={setFarm}
                    />
                  </div>
                </div>
                <div className="cart-divider">
                  <div />
                  <div className="total">
                    <div className="label">
                      AU{" "}
                      {parseFloat(totalPricing).toLocaleString("en-US", {
                        style: "currency",
                        currency: "USD",
                      })}
                    </div>
                    <div className="sub-label">Total price including taxes</div>
                  </div>
                </div>
                <div className="cart-footer">
                  <Button
                    type="outlined"
                    color="white"
                    label="Clear Cart"
                    block
                    onClick={() => handleDeleteAll()}
                  />
                  <Button
                    type="solid"
                    color="black"
                    label="Proceed to Checkout"
                    block
                    onClick={() => handleCheckOut(cartItems)}
                    disabled={!cartItems}
                  />
                </div>
              </>
            )}
          </>
        )}
      </div>
    </CustomModal>
  );
};

CartModal.defaultProps = {
  items: {},
  isOpen: false,
  onClose: () => {},
};

CartModal.propTypes = {
  items: PropTypes.shape({
    title: PropTypes.string,
    description: PropTypes.string,
  }),
  isOpen: PropTypes.bool,
  onClose: PropTypes.func,
};

export default CartModal;
